/**
 * Session 147j+1 — Per-symbol-per-pattern enable/disable matrix card.
 *
 * Renders a 2×4 toggle grid (NIFTY/BANKNIFTY × LQP/DHOL/POC_REJECT/
 * SLINGSHOT_EP). Disabled cells fully mute the corresponding pattern in
 * production: no Telegram DM, no daily-signals log entry, no broker
 * order. Resolution priority on the backend is:
 *
 *   matrix doc → legacy PATTERN_EXECUTION_ENABLED_<NAME> env → default ON
 *
 * Mounted inside StrategyTunerTab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Power, Save, RotateCcw, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SYMBOLS = ['NIFTY', 'BANKNIFTY'];
const PATTERNS = ['LQP', 'DHOL', 'POC_REJECT', 'SLINGSHOT_EP'];

const PATTERN_LABELS = {
  LQP: 'LQP',
  DHOL: 'DHOL',
  POC_REJECT: 'POC Reject',
  SLINGSHOT_EP: 'Slingshot EP',
};

const PATTERN_DESCS = {
  LQP: 'Liquidity Pool — primary order-flow setup, footprint absorption.',
  DHOL: 'Day High/Open Low — opening-range breakout pattern.',
  POC_REJECT: 'Point of Control rejection — auction value-area defence.',
  SLINGSHOT_EP: 'Slingshot to Excess Point — counter-trend reversal pattern.',
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
};

const PatternMatrixCard = () => {
  const [matrix, setMatrix] = useState(null);
  const [original, setOriginal] = useState(null);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null, is_default: true });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/strategy-tuner/pattern-matrix`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        setMatrix(d.matrix);
        setOriginal(JSON.parse(JSON.stringify(d.matrix)));
        setMeta({
          updated_at: d.updated_at,
          updated_by: d.updated_by,
          is_default: d.is_default,
        });
      }
    } catch (e) {
      setToast({ tone: 'err', msg: `Load failed: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (sym, pat) => {
    setMatrix((m) => ({
      ...m,
      [sym]: { ...m[sym], [pat]: !m[sym]?.[pat] },
    }));
  };

  const setColumn = (pat, value) => {
    setMatrix((m) => {
      const next = { ...m };
      for (const sym of SYMBOLS) {
        next[sym] = { ...next[sym], [pat]: value };
      }
      return next;
    });
  };

  const setRow = (sym, value) => {
    setMatrix((m) => ({
      ...m,
      [sym]: PATTERNS.reduce((acc, p) => ({ ...acc, [p]: value }), {}),
    }));
  };

  const isDirty = matrix && original
    && JSON.stringify(matrix) !== JSON.stringify(original);

  const reset = () => {
    if (original) setMatrix(JSON.parse(JSON.stringify(original)));
    setToast(null);
  };

  const save = async () => {
    if (!matrix) return;
    setSaving(true);
    setToast(null);
    try {
      const r = await fetch(`${API}/api/admin/strategy-tuner/pattern-matrix`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ matrix }),
      });
      const d = await r.json();
      if (d.error || d.status === 'error') {
        setToast({ tone: 'err', msg: `Save failed: ${d.error || 'unknown'}` });
      } else {
        setMatrix(d.matrix);
        setOriginal(JSON.parse(JSON.stringify(d.matrix)));
        setMeta({
          updated_at: d.updated_at,
          updated_by: d.updated_by,
          is_default: d.is_default,
        });
        setToast({ tone: 'ok', msg: `Saved. Changes take effect within 30s (live cache TTL).` });
      }
    } catch (e) {
      setToast({ tone: 'err', msg: `Save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !matrix) {
    return (
      <div className="panel p-6 flex items-center gap-3 text-muted-foreground" data-testid="pattern-matrix-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading pattern matrix…
      </div>
    );
  }

  if (!matrix) return null;

  return (
    <div className="panel p-6" data-testid="pattern-matrix-card">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Power className="w-5 h-5 text-accent" />
            Per-Symbol Pattern Execution Matrix
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Toggle individual cells to enable/disable a pattern for a specific
            index. Disabled cells fully mute Telegram, daily-signals log, and
            broker orders.
          </p>
          <div className="text-[10px] text-muted-foreground/80 mt-2 font-mono">
            {meta.is_default
              ? 'Using default matrix (all enabled). Click Save to persist a custom configuration.'
              : `Last updated ${(meta.updated_at || '').slice(0, 19).replace('T', ' ')} by ${meta.updated_by || '—'}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <button
              onClick={reset}
              data-testid="pattern-matrix-reset-btn"
              className="text-[11px] px-3 py-1.5 rounded border border-border hover:bg-surface-highlight transition-colors flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
          <button
            onClick={save}
            disabled={!isDirty || saving}
            data-testid="pattern-matrix-save-btn"
            className="text-[11px] px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-medium transition-colors flex items-center gap-1"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Symbol
              </th>
              {PATTERNS.map((pat) => (
                <th key={pat} className="p-2 min-w-[120px]" data-testid={`pattern-col-${pat}`}>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs font-mono font-semibold text-foreground">
                      {PATTERN_LABELS[pat]}
                    </span>
                    <span
                      className="text-[9px] text-muted-foreground/60 line-clamp-2 leading-tight max-w-[110px] text-center"
                      title={PATTERN_DESCS[pat]}
                    >
                      {PATTERN_DESCS[pat]}
                    </span>
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={() => setColumn(pat, true)}
                        className="text-[8px] px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        title="Enable for all symbols"
                      >
                        all on
                      </button>
                      <button
                        onClick={() => setColumn(pat, false)}
                        className="text-[8px] px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        title="Disable for all symbols"
                      >
                        all off
                      </button>
                    </div>
                  </div>
                </th>
              ))}
              <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Row
              </th>
            </tr>
          </thead>
          <tbody>
            {SYMBOLS.map((sym) => (
              <tr
                key={sym}
                className="border-b border-border/40 hover:bg-surface-highlight/40"
                data-testid={`pattern-matrix-row-${sym}`}
              >
                <td className="p-3 font-mono font-bold text-foreground">{sym}</td>
                {PATTERNS.map((pat) => {
                  const enabled = !!matrix[sym]?.[pat];
                  return (
                    <td key={pat} className="p-2 text-center">
                      <button
                        onClick={() => toggle(sym, pat)}
                        data-testid={`pattern-toggle-${sym}-${pat}`}
                        aria-pressed={enabled}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          enabled ? 'bg-emerald-500' : 'bg-zinc-600'
                        }`}
                        title={enabled ? 'Click to disable' : 'Click to enable'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <div className="text-[9px] mt-1 font-mono uppercase tracking-wider">
                        {enabled ? (
                          <span className="text-emerald-300">live</span>
                        ) : (
                          <span className="text-rose-300">muted</span>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="p-2 text-center">
                  <div className="flex flex-col gap-1 items-center">
                    <button
                      onClick={() => setRow(sym, true)}
                      className="text-[9px] px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    >
                      all on
                    </button>
                    <button
                      onClick={() => setRow(sym, false)}
                      className="text-[9px] px-2 py-0.5 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                    >
                      all off
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toast && (
        <div
          data-testid="pattern-matrix-toast"
          className={`mt-3 text-xs px-3 py-2 rounded ${
            toast.tone === 'ok'
              ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
              : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default PatternMatrixCard;
